import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getCourse, addCourse, updateCourse, clearCurrentCourse } from '../actions/courseActions';
import { getLessonsMinimal } from '../actions/lessonActions';
import { getSubmissionFormsMinimal } from '../actions/submissionFormActions';
import { getReinforcementsMinimal } from '../actions/reinforcementActions';
import CourseForm from '../components/content-management/CourseForm';
import ContentManagementSidebar from '../components/content-management/ContentManagementSidebar';
import { isAdminRole } from '../utils/roleHelpers';

const CourseEditor = ({
  auth: { user },
  course: { currentCourse, courseLoading },
  lesson: { minimalLessons },
  submissionForm: { minimalSubmissionForms },
  reinforcement: { minimalReinforcements },
  getCourse,
  addCourse,
  updateCourse,
  clearCurrentCourse,
  getLessonsMinimal,
  getSubmissionFormsMinimal,
  getReinforcementsMinimal
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);

  // Determine mode from URL path
  const isView = location.pathname.includes('/view');
  const isEdit = location.pathname.includes('/edit');
  const isNew = location.pathname.includes('/new');
  const mode = isView ? 'view' : isEdit ? 'edit' : 'new';

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  useEffect(() => {
    // Load all necessary data (minimal versions for better performance)
    getLessonsMinimal();
    getSubmissionFormsMinimal();
    getReinforcementsMinimal();

    // Load course data if editing or viewing
    if ((isEdit || isView) && id) {
      getCourse(id);
    }

    // Show form after short delay to ensure data is loaded
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 100);

    // Cleanup: clear current course when component unmounts
    return () => {
      clearTimeout(timer);
      clearCurrentCourse();
    };
  }, [id, isEdit, isView, getCourse, getLessonsMinimal, getSubmissionFormsMinimal, getReinforcementsMinimal, clearCurrentCourse]);

  const handleClose = () => {
    navigate('/content-management/courses');
  };

  const handleSubmit = async (courseData) => {
    try {
      if (isEdit && id) {
        await updateCourse(id, courseData);
      } else if (isNew) {
        await addCourse(courseData);
      }
      // Navigation handled by form component after success
    } catch (error) {
      console.error('Error submitting course:', error);
      throw error;
    }
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Courses') {
      navigate('/content-management/courses');
    } else {
      navigate('/content-management', { state: { activeTab: tabId } });
    }
  };

  // Show loading spinner while data is being fetched
  if ((isEdit || isView) && courseLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Courses"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pb-6 px-6 min-w-0">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
              <span className="ml-4 text-gray-600">Loading course...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form only after data is ready
  if (!showForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Courses"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pb-6 px-6 min-w-0">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="w-full bg-white rounded-lg shadow-sm flex flex-col md:flex-row">
        <ContentManagementSidebar
          activeTab="Courses"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 min-w-0">
          <CourseForm
            mode={mode}
            course={currentCourse}
            lessons={minimalLessons}
            submissionForms={minimalSubmissionForms}
            reinforcements={minimalReinforcements}
            userRole={user?.role}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  course: state.course,
  lesson: state.lesson,
  submissionForm: state.submissionForm,
  reinforcement: state.reinforcement
});

export default connect(
  mapStateToProps,
  {
    getCourse,
    addCourse,
    updateCourse,
    clearCurrentCourse,
    getLessonsMinimal,
    getSubmissionFormsMinimal,
    getReinforcementsMinimal
  }
)(CourseEditor);
